import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { ib, muted, table } from "../../ui/record";
import { archiveModal, field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";

// Settings › Home care client types (brief 1C-1, mockup settings/clienttypes): a new client
// starts from its type's defaults, and new types are configuration, not new screens. Add a type
// is fully self-serve: its name, its payer fields picked from the field kinds, its intake form,
// its care-rule defaults and the workflow its patients follow; and (brief 1C-4) its Physician
// orders (Required, Optional or Not used), what carries over to a new episode or recertification
// and where it starts, and how aide supervision is timed. Admin only; every change is in the
// history. The verbal-orders setting is company-wide: Home Care › Settings.

interface PayerField {
  key: string;
  label: string;
  kind: string;
  required: boolean;
  options?: string[];
  mask?: boolean;
}
interface CareRules {
  responseHours: number | null;
  responseWords: string;
  authorization: { needed: boolean; perService: boolean; words: string };
  signOff: string;
  printedNote: string;
  money: Record<string, string>;
}
interface TypeView {
  id: string;
  name: string;
  archived: boolean;
  payerFields: (PayerField & { kindLabel: string })[];
  careRules: CareRules;
  authorizations: string;
  responseWindow: string;
  reassessDays: number;
  physicianOrders: string;
  followOn: { recertification: boolean; carries: string[]; startsAt: string | null };
  aideSupervision: string;
  billing: Record<string, string>;
  billsBy: string;
  billsByLabel: string;
  workflow: { key: string; name: string; version: number | null; stages: string[] };
  intakeForm: {
    id: string;
    name: string;
    version: number;
    status: string;
    asked: number | null;
  } | null;
  clients: number;
}
interface Opt {
  value: string;
  label: string;
}
interface Answer {
  types: TypeView[];
  fieldKinds: Opt[];
  rules: { key: string; label: string; level: string }[];
  levels: Opt[];
  signOffs: Opt[];
  printedNotes: Opt[];
  billingWords: { key: string; label: string }[];
  forms: { id: string; name: string; usedAt: string; version: number; status: string }[];
  workflows: { key: string; name: string; version: number; stages: string[] }[];
  ordersUses: Opt[];
  carries: Opt[];
  billsBys: Opt[];
  supervisions: (Opt & { words: string })[];
  defaults: CareRules;
}

export async function clientTypesScreen(host: HTMLElement, ctx: ScreenContext) {
  const page = el("div");
  host.append(page);
  let data: Answer | null = null;

  async function load() {
    const res = await api<Answer>("GET", "/api/settings/client-types");
    if (!res.ok) {
      page.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Client types couldn't be loaded."),
      );
      return;
    }
    data = res.body;
    draw();
  }

  function draw() {
    if (!data) return;
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add type");
    add.addEventListener("click", () => editor(null));
    const live = data.types.filter((t) => !t.archived);
    page.replaceChildren(
      pageHead(ctx, "A new client starts from its type's defaults.", [add], null),
      table(
        [
          { h: "Type", f: (t) => el("b", {}, t.name) },
          { h: "Authorizations", f: (t) => t.authorizations },
          { h: "Response window", f: (t) => t.responseWindow },
          { h: "Recert", f: (t) => t.billing.recert || "–" },
          { h: "Bills by", f: (t) => t.billsByLabel },
          { h: "Unit", f: (t) => t.billing.unit || "–" },
        ],
        live,
        (t) => editor(t),
        "Home care client types",
      ),
    );
  }

  /** The type's editor: everything a type is, in one modal. */
  function editor(t: TypeView | null) {
    if (!data) return;
    const d = data;
    const rules: CareRules = structuredClone(t?.careRules ?? d.defaults);
    let fields: PayerField[] = structuredClone(t?.payerFields ?? []).map(
      ({ key, label, kind, required, options, mask }) => ({
        key,
        label,
        kind,
        required,
        options,
        mask,
      }),
    );
    const name = field("Name", t?.name ?? "", { maxlength: "60" });

    // Payer fields, each a name, a kind and required or not; a choice list has its options.
    const fieldRows = el("div", { "aria-label": "Payer details" });
    const drawFields = () => {
      fieldRows.replaceChildren(
        ...(fields.length
          ? fields.map((f, i) => {
              const label = el("input", {
                type: "text",
                value: f.label,
                maxlength: "60",
                "aria-label": `Payer detail ${i + 1} name`,
              });
              label.addEventListener("input", () => (f.label = label.value));
              const kind = el(
                "select",
                { "aria-label": `Payer detail ${i + 1} kind` },
                ...d.fieldKinds.map((k) =>
                  el("option", { value: k.value, selected: k.value === f.kind }, k.label),
                ),
              );
              kind.addEventListener("change", () => {
                f.kind = kind.value;
                drawFields();
              });
              const req = el("input", {
                type: "checkbox",
                checked: f.required,
                "aria-label": `${f.label || "Detail"} is required`,
              });
              req.addEventListener("change", () => (f.required = req.checked));
              const opts =
                f.kind === "choice"
                  ? (() => {
                      const o = el("input", {
                        type: "text",
                        value: (f.options ?? []).join(", "),
                        placeholder: "Options, separated by commas",
                        "aria-label": `${f.label || "Detail"} options`,
                      });
                      o.addEventListener(
                        "input",
                        () =>
                          (f.options = o.value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean)),
                      );
                      return o;
                    })()
                  : null;
              const remove = ib("x", `Take off ${f.label || "this detail"}`, () => {
                fields = fields.filter((x) => x !== f);
                drawFields();
              });
              return el(
                "div",
                {
                  class: "row",
                  style: "gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:6px",
                },
                label,
                kind,
                el(
                  "label",
                  { class: "small", style: "display:flex;gap:4px;align-items:center" },
                  req,
                  "Required",
                ),
                opts,
                remove,
              );
            })
          : [muted("No payer details yet.")]),
      );
    };
    drawFields();
    const addField = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("plus"),
      " Add a payer detail",
    );
    addField.addEventListener("click", () => {
      fields.push({ key: "", label: "", kind: "text", required: false });
      drawFields();
      (fieldRows.lastElementChild?.querySelector("input") as HTMLInputElement | null)?.focus();
    });

    // Care rules.
    const hours = field(
      "Response window (hours; empty: per plan)",
      rules.responseHours == null ? "" : String(rules.responseHours),
      {
        type: "number",
        min: "1",
        max: "720",
      },
    );
    const responseWords = field("Response window, as it shows", rules.responseWords, {
      maxlength: "40",
    });
    const authNeeded = el("input", {
      type: "checkbox",
      checked: rules.authorization.needed,
      id: "ct-auth",
    });
    const perService = el("input", {
      type: "checkbox",
      checked: rules.authorization.perService,
      id: "ct-per",
    });
    const authWords = field("Authorizations, as they show", rules.authorization.words, {
      maxlength: "80",
    });
    const signOff = select(
      "How a visit is signed off",
      d.signOffs.map((o) => [o.value, o.label] as const),
      rules.signOff,
    );
    const printed = select(
      "Printed visit notes show",
      d.printedNotes.map((o) => [o.value, o.label] as const),
      rules.printedNote,
    );
    const money = d.rules.map((r) => ({
      key: r.key,
      s: select(
        r.label,
        d.levels.map((l) => [l.value, l.label] as const),
        rules.money[r.key] ?? r.level,
      ),
    }));
    // Brief 1E-15: what a new client of this type bills by.
    const billsBy = select(
      "Bills by",
      d.billsBys.map((o) => [o.value, o.label] as const),
      t?.billsBy ?? "invoice",
    );
    const billing = d.billingWords.map((b) => ({
      key: b.key,
      f: field(b.label, t?.billing[b.key] ?? "", { maxlength: "120" }),
    }));
    // Reassessment (DECISIONS 10/23): every 30, 45 or 60 days, or the company's own number.
    const days = t?.reassessDays ?? 30;
    const reassess = select(
      "Reassess every",
      [
        ["30", "30 days"],
        ["45", "45 days"],
        ["60", "60 days"],
        ["custom", "Other number of days"],
      ] as const,
      [30, 45, 60].includes(days) ? String(days) : "custom",
    );
    const reassessOwn = field("Days", String(days), { type: "number", min: "1", max: "365" });
    const showOwn = () => {
      reassessOwn.el.hidden = reassess.input.value !== "custom";
    };
    reassess.input.addEventListener("change", showOwn);
    showOwn();
    const workflow = select(
      "Workflow its patients follow",
      d.workflows.map((w) => [w.key, `${w.name} · version ${w.version}`] as const),
      t?.workflow.key ?? "episode",
    );

    // Physician orders, the new episode and recertification, aide supervision (brief 1C-4).
    const orders = select(
      "Physician orders",
      d.ordersUses.map((o) => [o.value, o.label] as const),
      t?.physicianOrders ?? "not_used",
    );
    const follow = t?.followOn ?? {
      recertification: false,
      carries: ["payer", "consents"],
      startsAt: null,
    };
    const recert = select(
      "Recertification",
      [
        ["used", "Used"],
        ["not_used", "Not used"],
      ] as const,
      follow.recertification ? "used" : "not_used",
    );
    const carryBoxes = d.carries.map((c) => {
      const b = el("input", {
        type: "checkbox",
        value: c.value,
        checked: follow.carries.includes(c.value),
      });
      return { b, el: el("label", {}, b, ` ${c.label}`) };
    });
    const startsAt = select("The new episode starts at", [], null);
    const fillStages = () => {
      const stages = d.workflows.find((w) => w.key === workflow.input.value)?.stages ?? [];
      startsAt.input.replaceChildren(
        el("option", { value: "" }, "Where a new assessment is filled"),
        ...stages.map((st) => el("option", { value: st, selected: st === follow.startsAt }, st)),
      );
    };
    workflow.input.addEventListener("change", fillStages);
    fillStages();
    const supervision = select(
      "Aide supervision",
      d.supervisions.map((o) => [o.value, o.label] as const),
      t?.aideSupervision ?? "assists_medications",
    );
    const supWords = el("p", { class: "small muted", style: "margin:0" });
    const showSup = () => {
      supWords.textContent =
        d.supervisions.find((o) => o.value === supervision.input.value)?.words ?? "";
    };
    supervision.input.addEventListener("change", showSup);
    showSup();
    const intake = select(
      "Intake form",
      [
        ...(t ? [] : [["", "A new one for this type"] as const]),
        ...d.forms
          .filter((f) => f.usedAt === "Intake")
          .map(
            (f) =>
              [
                f.id,
                `${f.name} · version ${f.version} (${f.status === "approved" ? "Approved" : "Draft"})`,
              ] as const,
          ),
      ],
      t?.intakeForm?.id ?? "",
    );

    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      t ? " Save" : " Add type",
    );
    const actions: Node[] = [save];
    if (t)
      actions.push(
        ib("archive", `Archive ${t.name}`, () => {
          m.close();
          archiveModal(t.name, `/api/settings/client-types/${t.id}/archive`, load);
        }),
      );
    const m = openModal({
      title: t ? t.name : "Add a client type",
      sub: t
        ? `${t.clients} client${t.clients === 1 ? "" : "s"} of this type. They keep their own care rules.`
        : "A name to start; everything else can be set now or later.",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          name.el,
          el("h3", { class: "h3s" }, "Who pays"),
          el(
            "p",
            { class: "small muted", style: "margin:0 0 6px" },
            "What the payer card asks for this type. Patients keep the details they were saved with.",
          ),
          fieldRows,
          addField,
          el("h3", { class: "h3s" }, "Intake and workflow"),
          el("div", { class: "formgrid" }, intake.el, workflow.el),
          el("h3", { class: "h3s" }, "Care rules"),
          el(
            "div",
            { class: "formgrid" },
            hours.el,
            responseWords.el,
            el(
              "div",
              { class: "field" },
              el(
                "label",
                { for: "ct-auth", style: "display:flex;gap:6px;align-items:center" },
                authNeeded,
                "Authorizations needed",
              ),
              el(
                "label",
                { for: "ct-per", style: "display:flex;gap:6px;align-items:center" },
                perService,
                "One per service",
              ),
            ),
            authWords.el,
            signOff.el,
            printed.el,
            reassess.el,
            reassessOwn.el,
          ),
          el(
            "p",
            { class: "small muted", style: "margin:8px 0 4px" },
            "Flag care, hold the bill or the pay. Visits go on.",
          ),
          el("div", { class: "formgrid" }, ...money.map((x) => x.s.el)),
          el("h3", { class: "h3s" }, "Physician orders and aide supervision"),
          el(
            "p",
            { class: "small muted", style: "margin:0 0 6px" },
            "Required flags the episode until a signed order comes back.",
          ),
          el("div", { class: "formgrid" }, orders.el, el("div", {}, supervision.el, supWords)),
          el("h3", { class: "h3s" }, "New episode and recertification"),
          el(
            "div",
            { class: "formgrid" },
            recert.el,
            startsAt.el,
            el(
              "fieldset",
              { class: "field wide" },
              el("legend", {}, "What carries over"),
              el("div", { class: "checks" }, ...carryBoxes.map((x) => x.el)),
            ),
          ),
          el("h3", { class: "h3s" }, "Billing"),
          el(
            "div",
            { class: "formgrid" },
            el(
              "div",
              {},
              billsBy.el,
              el(
                "span",
                { class: "hint" },
                "A new client starts with this. Each client can change it on its Billing card.",
              ),
            ),
          ),
          el("h3", { class: "h3s" }, "Billing, as it shows until billing setup"),
          el("div", { class: "formgrid" }, ...billing.map((b) => b.f.el)),
        ),
      ],
      actions,
    });
    name.input.focus();
    save.addEventListener("click", async () => {
      save.disabled = true;
      const body = {
        name: name.input.value,
        payerFields: fields.map((f) => ({ ...f, key: f.key || undefined })),
        careRules: {
          responseHours: hours.input.value === "" ? null : Number(hours.input.value),
          responseWords: responseWords.input.value,
          authorization: {
            needed: authNeeded.checked,
            perService: perService.checked,
            words: authWords.input.value,
          },
          signOff: signOff.input.value,
          printedNote: printed.input.value,
          money: Object.fromEntries(money.map((x) => [x.key, x.s.input.value])),
        },
        billing: Object.fromEntries(billing.map((b) => [b.key, b.f.input.value])),
        billsBy: billsBy.input.value,
        workflowKey: workflow.input.value,
        reassessDays:
          reassess.input.value === "custom"
            ? Number(reassessOwn.input.value)
            : Number(reassess.input.value),
        ...(intake.input.value ? { intakeFormId: intake.input.value } : {}),
        physicianOrders: orders.input.value,
        followOn: {
          recertification: recert.input.value === "used",
          carries: carryBoxes.filter((x) => x.b.checked).map((x) => x.b.value),
          startsAt: startsAt.input.value || null,
        },
        aideSupervision: supervision.input.value,
      };
      const res = await api<Answer>(
        t ? "PUT" : "POST",
        t ? `/api/settings/client-types/${t.id}` : "/api/settings/client-types",
        body,
      );
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      data = res.body;
      draw();
      toast(
        t ? "Client type saved" : "Client type added",
        t ? undefined : "Its intake form is a draft until it's approved in Intake questions",
      );
    });
  }

  await load();
}
